import type {
  CockpitArtifactAccess,
  CockpitArtifactRole,
  CockpitConfidence,
  CockpitCoverage,
  CockpitGuaranteeResult,
  CockpitLifecycle,
  CockpitProvenance,
  CockpitRelationKind,
  CockpitSectionStatus,
  CockpitUsageObservationState,
  ManagementTimelineMarkerType,
  ManagementTimelineGroupBy,
  ManagementTimelineOutcome,
} from '@internal/spassu-code-contract';

export function activityStatusLabel(value: string): string {
  const labels: Record<string, string> = {
    active: 'Em andamento',
    running: 'Em andamento',
    completed: 'Concluída',
    waiting: 'Aguardando',
    blocked: 'Bloqueada',
    failed: 'Com falha',
    error: 'Com falha',
    cancelled: 'Cancelada',
  };
  return labels[value] ?? humanizeCode(value);
}

export function evidenceConditionLabel(
  status: CockpitSectionStatus,
  coverage: CockpitCoverage,
): string {
  if (status === 'failed') return 'Evidência com falha de leitura';
  if (status === 'unavailable') return 'Evidência indisponível';
  if (status === 'not-applicable') return 'Evidência não aplicável';
  if (status === 'empty') return 'Evidência ainda não observada';
  if (coverage === 'complete') return 'Evidência completa';
  if (coverage === 'partial') return 'Evidência parcial';
  return 'Evidência sem cobertura';
}

export function coverageLabel(value: CockpitCoverage): string {
  if (value === 'complete') return 'Cobertura completa';
  if (value === 'partial') return 'Cobertura parcial';
  return 'Sem cobertura';
}

export function confidenceLabel(value: CockpitConfidence): string {
  const labels: Record<CockpitConfidence, string> = {
    confirmed: 'Confirmado',
    calculated: 'Calculado',
    estimated: 'Estimado',
    detected: 'Detectado',
    inferred: 'Inferido',
    partial: 'Parcial',
    unavailable: 'Indisponível',
    'load-failed': 'Falha de leitura',
  };
  return labels[value];
}

export function lifecycleLabel(
  value: CockpitLifecycle | 'available' | 'deleted',
): string {
  const labels: Record<typeof value, string> = {
    running: 'Em execução',
    waiting: 'Aguardando',
    completed: 'Concluído',
    interrupted: 'Interrompido',
    unknown: 'Ciclo desconhecido',
    available: 'Disponível',
    deleted: 'Removido',
  };
  return labels[value];
}

export function artifactRoleLabel(value: CockpitArtifactRole): string {
  const labels: Record<CockpitArtifactRole, string> = {
    input: 'Entrada',
    'working-document': 'Documento de trabalho',
    output: 'Saída',
  };
  return labels[value];
}

export function artifactAccessLabel(value: CockpitArtifactAccess): string {
  const labels: Record<CockpitArtifactAccess, string> = {
    inspectable: 'Conteúdo consultável',
    'no-content': 'Sem conteúdo persistido',
    forbidden: 'Conteúdo restrito',
  };
  return labels[value];
}

export function relationKindLabel(value: CockpitRelationKind): string {
  const labels: Record<CockpitRelationKind, string> = {
    'used-as-input': 'Usado como entrada',
    produced: 'Produziu',
    'validated-by': 'Validado por',
    changed: 'Alterou',
    'committed-as': 'Registrado em commit',
    'delivered-by': 'Entregue por',
    supersedes: 'Substitui',
  };
  return labels[value];
}

export function sectionStatusLabel(value: CockpitSectionStatus): string {
  const labels: Record<CockpitSectionStatus, string> = {
    ready: 'Disponível',
    empty: 'Sem dados',
    partial: 'Parcial',
    unavailable: 'Indisponível',
    failed: 'Falha de leitura',
    'not-applicable': 'Não aplicável',
  };
  return labels[value];
}

export function usageObservationLabel(
  value: CockpitUsageObservationState,
): string {
  const labels: Record<CockpitUsageObservationState, string> = {
    'not-observed': 'Uso ainda não observado',
    'awaiting-stop': 'Aguardando evento final do Claude',
    flushing: 'Entrega de métricas em andamento',
    'timed-out': 'Concluída com métricas pendentes',
    failed: 'Coleta de métricas com falha',
    incompatible: 'Coletor Claude incompatível',
    rejected: 'Sinais rejeitados por origem incoerente',
    stale: 'Última leitura de integridade desatualizada',
    confirmed: 'Métricas confirmadas',
    'late-evidence': 'Atualizada por evidência tardia',
    'manual-reconciliation': 'Atualizada por recuperação autorizada',
  };
  return labels[value];
}

export function guaranteeStatusLabel(
  value: 'complete' | 'incomplete' | 'unavailable',
): string {
  if (value === 'complete') return 'Garantia completa';
  if (value === 'incomplete') return 'Garantia incompleta';
  return 'Garantia indisponível';
}

export function controlResultLabel(value: CockpitGuaranteeResult): string {
  const labels: Record<CockpitGuaranteeResult, string> = {
    passed: 'Atendido',
    failed: 'Não atendido',
    partial: 'Atendido parcialmente',
    missing: 'Evidência ausente',
    'not-applicable': 'Não aplicável',
    unknown: 'Resultado desconhecido',
  };
  return labels[value];
}

export function provenanceLabel(value: CockpitProvenance): string {
  const labels: Record<CockpitProvenance, string> = {
    'mcp-report': 'Relatório MCP',
    'agent-event': 'Evento do agente',
    'artifact-store': 'Repositório de artefatos',
    'phase-conformity': 'Conformidade de fase',
    policy: 'Política',
    scm: 'Repositório de código',
    reported: 'Valor reportado',
    calculated: 'Valor calculado',
    'legacy-metric': 'Métrica histórica',
    'cockpit-snapshot': 'Retrato do cockpit',
  };
  return labels[value];
}

export function limitationLabel(code: string): string {
  const labels: Record<string, string> = {
    'event-gaps-remain-unclassified':
      'Há intervalos sem classificação na janela observada.',
    'currency-unavailable':
      'O custo não pôde ser reconciliado porque a moeda não foi informada.',
    'invalid-usage-event-timestamp':
      'Evento de uso excluído porque o instante observado é inválido.',
    'invalid-usage-record-timestamp':
      'Registro de uso excluído porque o instante observado é inválido.',
  };
  return labels[code] ?? humanizeCode(code);
}

export function timelineTypeLabel(value: ManagementTimelineMarkerType): string {
  const labels: Record<ManagementTimelineMarkerType, string> = {
    activity: 'Atividade',
    session: 'Sessão',
    workflow: 'Fluxo de trabalho',
    skill: 'Skill',
    task: 'Task',
    worktree: 'Área de trabalho',
    artifact: 'Artefato',
    approval: 'Aprovação',
    vcs: 'Controle de versão',
    risk: 'Risco',
    observability_gap: 'Lacuna de observabilidade',
  };
  return labels[value];
}

export function timelineOutcomeLabel(value: ManagementTimelineOutcome): string {
  const labels: Record<ManagementTimelineOutcome, string> = {
    started: 'Iniciado',
    completed: 'Concluído',
    failed: 'Com falha',
    integrated: 'Integrado',
    published: 'Publicado',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    pending: 'Pendente',
    blocked: 'Bloqueado',
    warning: 'Atenção',
    unknown: 'Resultado desconhecido',
  };
  return labels[value];
}

export function timelineGroupByLabel(value: ManagementTimelineGroupBy): string {
  const labels: Record<ManagementTimelineGroupBy, string> = {
    type: 'Tipo',
    participant: 'Participante',
    skill: 'Skill',
    task: 'Task',
    outcome: 'Resultado',
    phase: 'Fase',
  };
  return labels[value];
}

export function humanizeCode(value: string): string {
  const normalized = value.replace(/[-_]+/g, ' ').trim();
  if (!normalized) return 'Informação não detalhada';
  return `${normalized.charAt(0).toLocaleUpperCase('pt-BR')}${normalized.slice(
    1,
  )}`;
}
